import { shortenShopifyId } from '../../app/_utils/shortenShopifyId'

import type { SessionAttributes } from '../../app/_config/Session.config'

const sourcePlatform = 'web' as const

/**
 * Iterable cart line payload (commerce `updateCart` + custom add/remove events).
 */
export type IterableCartLinePayload = {
  title: string
  category: string
  gender: string
  color: string
  size: string
  fit: string
  imageUrl: string
  effectivePriceInCents: number
  quantity: number
  shopifyProductGuid: string
  shopifyVariantGuid: string
  sku: string
  isAvailableForSale: boolean
  productName: string
  productHandle: string
}

export const iterableCartItemAddedEventName = 'figs_cart_item_added' as const
export const iterableCartItemRemovedEventName = 'figs_cart_item_removed' as const

type TrackedLike = {
  effectivePrice: number
  quantity: number
  productShopifyId: string
  variantShopifyId: string
  variantSku: string
  handle: string
  categoryId: string
  colorId: string | undefined | null
  sizeId: string | undefined | null
  fitId: string | undefined | null
  gender?: string
  imageUrl?: string
  availableForSale?: boolean
  title?: string
}

export const toIterableCartLinePayload = (item: TrackedLike): IterableCartLinePayload => {
  const titleTrim = item.title?.trim()
  const handleTrim = item.handle.trim()
  const productName = titleTrim || item.variantSku
  const productHandle = handleTrim || item.variantSku
  const displayTitle = productName

  return {
    title: displayTitle,
    category: item.categoryId,
    gender: item.gender ?? '',
    color: item.colorId ?? '',
    size: item.sizeId ?? '',
    fit: item.fitId ?? '',
    imageUrl: item.imageUrl ?? '',
    effectivePriceInCents: item.effectivePrice,
    quantity: item.quantity,
    shopifyProductGuid: item.productShopifyId,
    shopifyVariantGuid: item.variantShopifyId,
    sku: item.variantSku,
    isAvailableForSale: item.availableForSale ?? true,
    productName,
    productHandle,
  }
}

export const buildCommerceCartItem = (
  line: IterableCartLinePayload,
  stylePreference: SessionAttributes['stylePreference']
) => {
  const variantId = shortenShopifyId(line.shopifyVariantGuid)
  const productId = shortenShopifyId(line.shopifyProductGuid)

  return {
    categories: [line.category],
    sourcePlatform,
    dataFields: {
      variantId,
      productId,
      productGender: line.gender,
      color: line.color,
      genderPreference: stylePreference,
      availability: line.isAvailableForSale,
      size: line.size,
      sourcePlatform,
      productName: line.productName,
      productHandle: line.productHandle,
    },
    id: variantId,
    imageUrl: line.imageUrl,
    name: line.title,
    price: line.effectivePriceInCents / 100,
    quantity: line.quantity,
    sku: line.sku,
  }
}

/** Flattened line for Iterable custom events (aligned with commerce line fields). */
export const toIterableCartLineEventDataFields = (
  line: IterableCartLinePayload,
  stylePreference: SessionAttributes['stylePreference'],
  extras?: { cartId?: string | null; previousQuantity?: number }
): Record<string, unknown> => {
  const commerce = buildCommerceCartItem(line, stylePreference)
  const flat: Record<string, unknown> = {
    ...commerce.dataFields,
    id: commerce.id,
    categories: commerce.categories,
    imageUrl: commerce.imageUrl,
    name: commerce.name,
    price: commerce.price,
    quantity: commerce.quantity,
    sku: commerce.sku,
  }
  if (extras?.cartId !== undefined && extras.cartId !== null) {
    flat.cartId = extras.cartId
  }
  if (extras?.previousQuantity !== undefined) {
    flat.previousQuantity = extras.previousQuantity
  }
  return flat
}
